import { useAuth0 } from '@auth0/auth0-react'
import { useLocation } from '@gatsbyjs/reach-router'
import queryString from 'query-string'
import { pipe } from 'ramda'
import { Reducer, useEffect, useMemo, useReducer } from 'react'
import { defaultIfEmpty, filter, from, map, switchMap } from 'rxjs'

import {
  arbitrateRedirect$,
  RedirectHandlerSelection,
  registerRedirectHandler,
  submitRedirectEvidence,
} from '../../auth/logging-in'
import { addDispensaryBasePathToPath, getDispensarySlugFromPath } from '../../dispensaries'
import { dispensaryExternalEffect$ } from '../../dispensaries/effects'
import { usePageUrl } from '../../pages'

export interface UseAccountsCallbackProps {
  // path to redirect in accountsReturnTo unavailable and auth error occurs
  fallbackRedirectPath?: string
}

export interface UseAccountsCallback {
  accountsReturnTo: string | undefined
}

type CallbackAction = { type: 'user_session_refreshed' }

interface CallbackState {
  hasRefreshedUserSession: boolean
}

const initialState: CallbackState = {
  hasRefreshedUserSession: false,
}

const callbackReducer: Reducer<CallbackState, CallbackAction> = (prevState, action) => {
  switch (action.type) {
    case 'user_session_refreshed':
      return prevState.hasRefreshedUserSession
        ? prevState
        : { ...prevState, hasRefreshedUserSession: true }
    default:
      return prevState
  }
}

const firstOrIdentity = (x: string | string[] | undefined): string | undefined =>
  Array.isArray(x) ? x[0] : x

const pickAccountsReturnTo = ({
  accountsReturnTo,
}: queryString.ParsedQuery<string>): string | string[] | undefined => accountsReturnTo ?? undefined

const coerceEmptyToUndefined = (x: string | undefined): string | undefined => (x ? x : undefined)

const accountsReturnToFrom = pipe(pickAccountsReturnTo, firstOrIdentity, coerceEmptyToUndefined)

export const useAccountsCallback = ({
  fallbackRedirectPath,
}: UseAccountsCallbackProps): UseAccountsCallback => {
  const { search } = useLocation()
  const { navigate } = usePageUrl()
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0()
  const [{ hasRefreshedUserSession }, dispatch] = useReducer(callbackReducer, initialState)

  const accountsReturnTo = useMemo(() => accountsReturnToFrom(queryString.parse(search)), [search])

  const prefixedFallbackPath = useMemo(() => {
    const dispensarySlug = accountsReturnTo
      ? getDispensarySlugFromPath(accountsReturnTo)
      : undefined
    return dispensarySlug
      ? addDispensaryBasePathToPath(dispensarySlug, fallbackRedirectPath)
      : fallbackRedirectPath
  }, [accountsReturnTo, fallbackRedirectPath])

  useEffect(() => {
    registerRedirectHandler('accounts_redirect')
    registerRedirectHandler('fallback')
  }, [])

  useEffect(() => {
    // default to `null` to distinguish between not present and not submitted
    submitRedirectEvidence({ accountsReturnTo: accountsReturnTo ?? null })
  }, [accountsReturnTo])

  useEffect((): void | (() => void) => {
    if (!isLoading && !isAuthenticated && !hasRefreshedUserSession) {
      const subscription = from(
        // use getAccessTokenSilently() to force Auth0 request (without redirecting to login)
        getAccessTokenSilently().catch(() => {
          // ignore any authentication error
        }),
      ).subscribe(() => {
        dispatch({ type: 'user_session_refreshed' })
      })
      return () => subscription.unsubscribe()
    }
  }, [getAccessTokenSilently, hasRefreshedUserSession, isAuthenticated, isLoading])

  useEffect(() => {
    if (!isLoading && (isAuthenticated || hasRefreshedUserSession)) {
      // submit `isAuthenticated` now that we can assert true or false
      submitRedirectEvidence({ isAuthenticated })
    }
  }, [hasRefreshedUserSession, isAuthenticated, isLoading])

  useEffect(() => {
    const subscription = arbitrateRedirect$
      .pipe(
        filter(
          (
            selection,
          ): selection is Extract<
            RedirectHandlerSelection,
            {
              handler: 'accounts_redirect' | 'fallback'
            }
          > => selection?.handler === 'accounts_redirect' || selection?.handler === 'fallback',
        ),
        map((selection) =>
          selection.handler === 'accounts_redirect'
            ? selection.accountsReturnTo
            : prefixedFallbackPath ?? '/',
        ),
        switchMap((returnTo) =>
          // proactively check for a dispensary redirect to avoid a double redirect (i.e. if
          // the user will be redirected to a dispensary, take them there directly)
          dispensaryExternalEffect$.pipe(
            // exclude `notify` effect
            filter(({ _tag }) => _tag === 'redirect'),
            map(({ dispensary }) => {
              // take the user directly to their dispensary
              const dispensaryReturnTo = addDispensaryBasePathToPath(dispensary.slug, returnTo)
              return () => navigate(dispensaryReturnTo, { replace: true })
            }),
            defaultIfEmpty(() =>
              // Use Gatsby's navigate method to replace the url
              navigate(returnTo, { replace: true }),
            ),
          ),
        ),
      )
      .subscribe((effect) => effect())
    return () => subscription.unsubscribe()
  }, [navigate, prefixedFallbackPath])

  return { accountsReturnTo }
}
